import { useEffect, useRef, useState, type ReactNode } from 'react';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import CodeIcon from '@mui/icons-material/Code';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LinkIcon from '@mui/icons-material/Link';
import RedoIcon from '@mui/icons-material/Redo';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import UndoIcon from '@mui/icons-material/Undo';
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, FormHelperText, IconButton, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/react';
import DOMPurify from 'dompurify';
import { useInput, useNotify, type InputProps } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import {
    ADMIN_BLOG_ASSET_ACCEPT,
    getAdminBlogAssetRuleSummary,
    importAdminBlogHtmlZip,
    resolveAdminBlogAssetUrl,
    uploadAdminBlogAsset,
    validateAdminBlogAssetFile,
} from '../admin/adminBlogDataProvider';
import './BlogContentEditor.css';

type ToolbarIconButtonProps = {
    title: string;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    children: ReactNode;
};

function sanitizeBlogContent(value: string): string {
    return DOMPurify.sanitize(value, {
        ADD_ATTR: ['style'],
        ADD_TAGS: ['style'],
        FORBID_TAGS: ['script'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onchange', 'onsubmit', 'onmouseover', 'onfocus'],
    });
}

function hasEmbeddedStyle(value: string): boolean {
    return /<style\b/i.test(value);
}

function normalizeEditorValue(value: unknown): string {
    const rawValue = String(value ?? '').trim();
    return rawValue ? rawValue : '<p></p>';
}

function shouldPreferHtmlMode(value: unknown): boolean {
    const html = String(value ?? '');
    return /<style\b|<article\b|<section\b|<main\b|<div\b|\sstyle\s=/i.test(html);
}

function ToolbarIconButton({ title, active = false, disabled = false, onClick, children }: ToolbarIconButtonProps) {
    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    size="small"
                    color={active ? 'primary' : 'default'}
                    disabled={disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onClick}
                    sx={{
                        border: '1px solid',
                        borderColor: active ? 'primary.main' : 'rgba(148, 163, 184, 0.4)',
                        borderRadius: 2,
                        backgroundColor: active ? 'rgba(8, 145, 178, 0.12)' : 'rgba(255, 255, 255, 0.82)',
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

type BlogContentInputProps = InputProps & {
    helperText?: string;
};

type BlogContentEditorProps = {
    id?: string;
    label?: string;
    value?: string | null;
    onChange: (value: string) => void;
    onBlur?: () => void;
    helperText?: string;
    required?: boolean;
    invalid?: boolean;
    errorMessage?: string;
    onImportedPage?: (metadata: { title?: string; excerpt?: string }) => void;
};

export function BlogContentEditor({
    id,
    label,
    value,
    onChange,
    onBlur,
    helperText,
    required = false,
    invalid = false,
    errorMessage,
    onImportedPage,
}: BlogContentEditorProps) {
    const notify = useNotify();
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const zipImportInputRef = useRef<HTMLInputElement | null>(null);
    const htmlTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isUploadingAsset, setIsUploadingAsset] = useState(false);
    const [isImportingZip, setIsImportingZip] = useState(false);
    const [editorMode, setEditorMode] = useState<'visual' | 'html'>(() => (shouldPreferHtmlMode(value) ? 'html' : 'visual'));
    const [contentTab, setContentTab] = useState<'editor' | 'preview'>('editor');
    const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Placeholder.configure({
                placeholder: 'Write a polished article with headings, tables, links, images, and checklists.',
            }),
            Underline,
            Highlight.configure({ multicolor: true }),
            Link.configure({
                autolink: true,
                linkOnPaste: true,
                openOnClick: false,
                defaultProtocol: 'https',
            }),
            Image.configure({ allowBase64: true }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            TaskList,
            TaskItem.configure({ nested: true }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content: normalizeEditorValue(value),
        editorProps: {
            attributes: {
                class: 'blog-content-editor__canvas',
            },
            handleDOMEvents: {
                blur: () => {
                    onBlur?.();
                    return false;
                },
            },
        },
        onUpdate: ({ editor: currentEditor }) => {
            onChange(currentEditor.isEmpty ? '' : currentEditor.getHTML());
        },
    });

    useEffect(() => {
        if (!editor || editorMode === 'html') {
            return;
        }

        const nextValue = normalizeEditorValue(value);
        if (editor.getHTML() !== nextValue) {
            editor.commands.setContent(nextValue, { emitUpdate: false });
        }
    }, [editor, editorMode, value]);

    useEffect(() => {
        if (shouldPreferHtmlMode(value)) {
            setEditorMode('html');
        }
    }, [value]);

    const previewHtml = String(value ?? '');
    const editorUnavailable = !editor;

    const handleLinkClick = () => {
        if (!editor) {
            return;
        }

        const previousHref = String(editor.getAttributes('link').href ?? '').trim();
        const nextHref = window.prompt('Enter a link URL. Leave empty to remove the current link.', previousHref);

        if (nextHref === null) {
            return;
        }

        const trimmedHref = nextHref.trim();
        if (!trimmedHref) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: trimmedHref, target: '_blank', rel: 'noopener noreferrer nofollow' })
            .run();
    };

    const handleImageUrlClick = () => {
        if (!editor) {
            return;
        }

        const source = window.prompt('Enter an image URL');
        const trimmedSource = source?.trim();

        if (!trimmedSource) {
            return;
        }

        editor.chain().focus().setImage({ src: trimmedSource, alt: 'Blog image' }).run();
    };

    const handleAssetPickerClick = () => {
        if ((!editor && editorMode === 'visual') || isUploadingAsset) {
            return;
        }

        uploadInputRef.current?.click();
    };

    const handleZipImportPickerClick = () => {
        if (isImportingZip) {
            return;
        }

        zipImportInputRef.current?.click();
    };

    const insertIntoHtml = (snippet: string) => {
        const currentValue = String(value ?? '');
        const textarea = htmlTextareaRef.current;

        if (!textarea) {
            onChange(`${currentValue}${snippet}`);
            return;
        }

        const selectionStart = textarea.selectionStart;
        const selectionEnd = textarea.selectionEnd;
        const nextValue = `${currentValue.slice(0, selectionStart)}${snippet}${currentValue.slice(selectionEnd)}`;
        onChange(nextValue);

        window.requestAnimationFrame(() => {
            textarea.focus();
            const nextCursor = selectionStart + snippet.length;
            textarea.setSelectionRange(nextCursor, nextCursor);
        });
    };

    const handleAssetUploadChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = '';

        if (!selectedFile) {
            return;
        }

        const validationMessage = validateAdminBlogAssetFile(selectedFile);
        if (validationMessage) {
            notify(validationMessage, { type: 'error' });
            return;
        }

        setIsUploadingAsset(true);

        try {
            const uploadResult = await uploadAdminBlogAsset(selectedFile);
            const assetUrl = resolveAdminBlogAssetUrl(uploadResult.fileUrl);
            const isImageAsset = uploadResult.contentType.toLowerCase().startsWith('image/');

            if (editorMode === 'html') {
                const snippet = isImageAsset
                    ? `<img src="${assetUrl}" alt="${uploadResult.fileName}">`
                    : `<a href="${assetUrl}" target="_blank" rel="noopener noreferrer nofollow">${uploadResult.fileName}</a>`;
                insertIntoHtml(snippet);
                notify('Asset uploaded and inserted into HTML', { type: 'info' });
            } else if (editor && isImageAsset) {
                editor.chain().focus().setImage({ src: assetUrl, alt: uploadResult.fileName }).run();
                notify('Image uploaded and inserted', { type: 'info' });
            } else if (editor) {
                editor
                    .chain()
                    .focus()
                    .insertContent(`<p><a href="${assetUrl}" target="_blank" rel="noopener noreferrer nofollow">${uploadResult.fileName}</a></p>`)
                    .run();
                notify('File uploaded and linked', { type: 'info' });
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Asset upload failed';
            notify(message, { type: 'error' });
        } finally {
            setIsUploadingAsset(false);
        }
    };

    const handleZipImportChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];
        event.target.value = '';

        if (!selectedFile) {
            return;
        }

        if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
            notify('Please choose a .zip file.', { type: 'error' });
            return;
        }

        setIsImportingZip(true);

        try {
            const imported = await importAdminBlogHtmlZip(selectedFile);
            setEditorMode('html');
            onChange(imported.content);
            onImportedPage?.({ title: imported.title, excerpt: imported.excerpt });
            notify('HTML package imported successfully', { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Zip import failed';
            notify(message, { type: 'error' });
        } finally {
            setIsImportingZip(false);
        }
    };

    const helperMessage =
        errorMessage ??
        helperText ??
        `Supports headings, task lists, tables, code blocks, uploaded assets, links, and images by URL. ${getAdminBlogAssetRuleSummary()}`;

    return (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1.5}>
                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', lg: 'center' }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography component="label" htmlFor={id} sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {label ?? 'Content'}
                            {required ? ' *' : ''}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.4 }}>
                            Structured authoring with live preview for long-form blog pages.
                        </Typography>
                    </Box>
                    <Alert
                        severity="info"
                        sx={{
                            width: { xs: '100%', lg: 'auto' },
                            borderRadius: 3,
                            backgroundColor: 'rgba(236, 254, 255, 0.9)',
                        }}
                    >
                        Use the toolbar to build Confluence-style pages with rich sections and layout content.
                    </Alert>
                </Stack>

                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: 'rgba(148, 163, 184, 0.28)',
                        overflow: 'hidden',
                    }}
                >
                    <Tabs
                        value={contentTab}
                        onChange={(_, nextTab: 'editor' | 'preview') => setContentTab(nextTab)}
                        aria-label="Content editor tabs"
                        variant="fullWidth"
                    >
                        <Tab value="editor" label="Editor" />
                        <Tab value="preview" label="Live Preview" />
                    </Tabs>
                </Paper>

                {contentTab === 'editor' ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        borderRadius: 4,
                        borderColor: invalid ? 'error.main' : 'rgba(148, 163, 184, 0.28)',
                        background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.82) 100%)',
                    }}
                >
                    <Stack spacing={1.5}>
                        <input
                            ref={uploadInputRef}
                            type="file"
                            hidden
                            accept={ADMIN_BLOG_ASSET_ACCEPT}
                            onChange={(event) => {
                                void handleAssetUploadChange(event);
                            }}
                        />
                        <input
                            ref={zipImportInputRef}
                            type="file"
                            hidden
                            accept=".zip"
                            onChange={(event) => {
                                void handleZipImportChange(event);
                            }}
                        />
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            <Button
                                size="small"
                                variant={editorMode === 'visual' ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                    setEditorMode('visual');
                                    editor?.commands.setContent(normalizeEditorValue(value), { emitUpdate: false });
                                }}
                                disabled={editorUnavailable}
                            >
                                Visual Editor
                            </Button>
                            <Button
                                size="small"
                                variant={editorMode === 'html' ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => setEditorMode('html')}
                            >
                                HTML Editor
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleZipImportPickerClick}
                                disabled={isImportingZip}
                            >
                                Upload Page Zip
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CloudUploadOutlinedIcon />}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleAssetPickerClick}
                                disabled={isUploadingAsset || isImportingZip}
                            >
                                Upload Asset
                            </Button>
                            {editorMode === 'visual' ? (
                                <>
                            <Button
                                size="small"
                                variant={editor?.isActive('paragraph') ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => editor?.chain().focus().setParagraph().run()}
                                disabled={editorUnavailable}
                            >
                                Body
                            </Button>
                            <Button
                                size="small"
                                variant={editor?.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                                disabled={editorUnavailable}
                            >
                                H1
                            </Button>
                            <Button
                                size="small"
                                variant={editor?.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                                disabled={editorUnavailable}
                            >
                                H2
                            </Button>
                            <Button
                                size="small"
                                variant={editor?.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                                disabled={editorUnavailable}
                            >
                                H3
                            </Button>
                            <ToolbarIconButton
                                title="Bold"
                                active={editor?.isActive('bold')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleBold().run()}
                            >
                                <FormatBoldIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Italic"
                                active={editor?.isActive('italic')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleItalic().run()}
                            >
                                <FormatItalicIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Underline"
                                active={editor?.isActive('underline')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                            >
                                <FormatUnderlinedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Strike"
                                active={editor?.isActive('strike')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleStrike().run()}
                            >
                                <StrikethroughSIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Highlight"
                                active={editor?.isActive('highlight')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleHighlight().run()}
                            >
                                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>H</Typography>
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Bullet List"
                                active={editor?.isActive('bulletList')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                            >
                                <FormatListBulletedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Numbered List"
                                active={editor?.isActive('orderedList')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                            >
                                <FormatListNumberedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Task List"
                                active={editor?.isActive('taskList')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleTaskList().run()}
                            >
                                <TaskAltIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Quote"
                                active={editor?.isActive('blockquote')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                            >
                                <FormatQuoteIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Code Block"
                                active={editor?.isActive('codeBlock')}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                            >
                                <CodeIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Divider"
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().setHorizontalRule().run()}
                            >
                                <HorizontalRuleIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Align Left"
                                active={editor?.isActive({ textAlign: 'left' })}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                            >
                                <FormatAlignLeftIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Align Center"
                                active={editor?.isActive({ textAlign: 'center' })}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                            >
                                <FormatAlignCenterIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Align Right"
                                active={editor?.isActive({ textAlign: 'right' })}
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                            >
                                <FormatAlignRightIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Set Link"
                                active={editor?.isActive('link')}
                                disabled={editorUnavailable}
                                onClick={handleLinkClick}
                            >
                                <LinkIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title={isUploadingAsset ? 'Uploading Asset' : 'Upload Asset'}
                                disabled={editorUnavailable || isUploadingAsset || isImportingZip}
                                onClick={handleAssetPickerClick}
                            >
                                <CloudUploadOutlinedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Insert Image By URL"
                                disabled={editorUnavailable || isUploadingAsset}
                                onClick={handleImageUrlClick}
                            >
                                <ImageOutlinedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Insert Table"
                                disabled={editorUnavailable}
                                onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                            >
                                <TableChartOutlinedIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Undo"
                                disabled={!editor?.can().undo()}
                                onClick={() => editor?.chain().focus().undo().run()}
                            >
                                <UndoIcon fontSize="small" />
                            </ToolbarIconButton>
                            <ToolbarIconButton
                                title="Redo"
                                disabled={!editor?.can().redo()}
                                onClick={() => editor?.chain().focus().redo().run()}
                            >
                                <RedoIcon fontSize="small" />
                            </ToolbarIconButton>
                                </>
                            ) : null}
                        </Stack>

                        {editorMode === 'html' ? (
                            <TextField
                                id={id}
                                inputRef={htmlTextareaRef}
                                value={String(value ?? '')}
                                onChange={(event) => onChange(event.target.value)}
                                onBlur={onBlur}
                                multiline
                                minRows={18}
                                fullWidth
                                placeholder="<article>...</article>"
                                slotProps={{
                                    input: {
                                        sx: {
                                            alignItems: 'flex-start',
                                            fontFamily: '"Cascadia Code", Consolas, "Courier New", monospace',
                                            fontSize: '0.88rem',
                                            lineHeight: 1.65,
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <Box id={id} className="blog-content-editor__shell">
                                <EditorContent editor={editor} />
                            </Box>
                        )}
                        {isUploadingAsset ? (
                            <Typography sx={{ color: '#0f766e', fontSize: '0.85rem', fontWeight: 600 }}>
                                Uploading asset and preparing insertion...
                            </Typography>
                        ) : null}
                        {isImportingZip ? (
                            <Typography sx={{ color: '#0f766e', fontSize: '0.85rem', fontWeight: 600 }}>
                                Validating page zip and uploading assets...
                            </Typography>
                        ) : null}
                    </Stack>
                </Paper>
                ) : null}

                {contentTab === 'preview' ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 4,
                        borderColor: 'rgba(148, 163, 184, 0.28)',
                        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                            <Box>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
                                    Live page preview
                                </Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.84rem', mt: 0.35 }}>
                                    This is how the article body will render from the stored HTML content.
                                </Typography>
                            </Box>
                            <Tooltip title="Fullscreen Preview">
                                <IconButton
                                    aria-label="Fullscreen preview"
                                    onClick={() => setIsPreviewFullscreen(true)}
                                    sx={{
                                        border: '1px solid rgba(148, 163, 184, 0.4)',
                                        borderRadius: 2,
                                        backgroundColor: '#ffffff',
                                    }}
                                >
                                    <FullscreenIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <BlogContentHtml value={previewHtml} isSanitized />
                    </Stack>
                </Paper>
                ) : null}

                <Dialog
                    fullScreen
                    open={isPreviewFullscreen}
                    onClose={() => setIsPreviewFullscreen(false)}
                    PaperProps={{ sx: { backgroundColor: '#f8fafc' } }}
                >
                    <DialogTitle sx={{ px: 3, py: 1.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Typography sx={{ fontWeight: 800 }}>Live page preview</Typography>
                            <Tooltip title="Close Preview">
                                <IconButton aria-label="Close fullscreen preview" onClick={() => setIsPreviewFullscreen(false)}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ p: { xs: 1.5, md: 3 } }}>
                        <BlogContentHtml value={previewHtml} isSanitized fullscreen />
                    </DialogContent>
                </Dialog>

                <FormHelperText error={invalid}>{helperMessage}</FormHelperText>
            </Stack>
        </Box>
    );
}

export function BlogContentInput(props: BlogContentInputProps) {
    const form = useFormContext();
    const { field, fieldState, id, isRequired } = useInput({
        ...props,
        defaultValue: '<p></p>',
    });

    const handleImportedPage = (metadata: { title?: string; excerpt?: string }) => {
        const currentTitle = String(form.getValues('title') ?? '').trim();
        const currentExcerpt = String(form.getValues('excerpt') ?? '').trim();

        if (!currentTitle && metadata.title) {
            form.setValue('title', metadata.title, { shouldDirty: true, shouldValidate: true });
        }

        if (!currentExcerpt && metadata.excerpt) {
            form.setValue('excerpt', metadata.excerpt, { shouldDirty: true, shouldValidate: true });
        }
    };

    return (
        <BlogContentEditor
            id={id}
            label={typeof props.label === 'string' ? props.label : undefined}
            value={String(field.value ?? '')}
            onChange={field.onChange}
            onBlur={field.onBlur}
            helperText={props.helperText}
            required={isRequired}
            invalid={fieldState.invalid}
            errorMessage={fieldState.error?.message}
            onImportedPage={handleImportedPage}
        />
    );
}

type BlogContentHtmlProps = {
    value?: string | null;
    isSanitized?: boolean;
    fullscreen?: boolean;
};

export function BlogContentHtml({ value, isSanitized = false, fullscreen = false }: BlogContentHtmlProps) {
    const rawHtml = String(value ?? '');

    if (hasEmbeddedStyle(rawHtml)) {
        return (
            <Box
                component="iframe"
                title="Blog content preview"
                srcDoc={rawHtml || '<p>No content available.</p>'}
                sandbox=""
                sx={{
                    display: 'block',
                    width: '100%',
                    minHeight: fullscreen ? 'calc(100vh - 112px)' : { xs: 640, md: 860 },
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    borderRadius: 2,
                    backgroundColor: '#ffffff',
                }}
            />
        );
    }

    const html = isSanitized ? rawHtml : sanitizeBlogContent(rawHtml);

    return (
        <Box
            className="blog-content-rendered"
            sx={{ color: '#0f172a' }}
            dangerouslySetInnerHTML={{
                __html: html || '<p class="blog-content-editor__empty">No content available.</p>',
            }}
        />
    );
}
